import React, {useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import classNames from 'classnames'
import {Button, Checkbox, Input, InputNumber, message, Select} from 'antd'
import {FaFileExport, FaFileImport} from 'react-icons/fa6'
import {IoRefreshOutline} from 'react-icons/io5'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {callApi} from 'modules/asyncCache/callApi'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems} from 'views/Shared'
import LoadingBlock from 'views/Loading/LoadingBlock'
import {
    course_getCourseStudents_Api,
    courseImportExport_applyImport_Api,
    courseImportExport_exportTemplate_Api,
    courseImportExport_previewImport_Api,
} from 'apis/course'
import {
    computeDefaultRowCount,
    CourseClassPicker,
    InfoBanner,
    ModeToggle,
    PreviewRowCard
} from './CourseImportExportShared'
import {DEDUP_METHOD_OPTIONS, STUDENT_IMPORT_MODES} from './course_import_export_config'

const STUDENT_MODE_OPTIONS = [
    {value: STUDENT_IMPORT_MODES.INSERT, label: 'student import mode insert'},
    {value: STUDENT_IMPORT_MODES.UPSERT, label: 'student import mode upsert'},
]

// Chọn phương thức chống trùng - CHỈ Học viên cần (Điểm danh/Điểm số không có nhánh "tạo học viên
// mới" nên không cần) - dùng chung Xuất (gợi ý) & Nhập (bắt buộc, gửi lên BE).
const DedupMethodPicker = ({value, onChange, t}) => (
    <div className="flex flex-col gap-1.5">
        <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('dedup method label')}</span>
        <Select
            size="large"
            value={value}
            onChange={onChange}
            options={DEDUP_METHOD_OPTIONS.map((o) => ({value: o.value, label: t(o.label)}))}
        />
        <InfoBanner>{t(DEDUP_METHOD_OPTIONS.find((o) => o.value === value)?.hint || DEDUP_METHOD_OPTIONS[0].hint)}</InfoBanner>
    </div>
)

// ─── Tab Xuất ───────────────────────────────────────────────────────────────────────────────────
export const StudentExportPanel = ({entity, owner_id, owner_type, t}) => {
    const [mode, setMode] = useState(STUDENT_IMPORT_MODES.INSERT)
    const [course, setCourse] = useState(null)
    const [courseClass, setCourseClass] = useState(null)
    const [dedupMethod, setDedupMethod] = useState(DEDUP_METHOD_OPTIONS[0].value)
    const [rowCount, setRowCount] = useState(50)
    const [rowCountTouched, setRowCountTouched] = useState(false)

    // Số dòng trống mặc định = quota còn lại của Lớp đã chọn (ưu tiên) hoặc Khóa, không có quota nào
    // thì mặc định 50 - vẫn cho sửa tay (rowCountTouched chặn việc default ghi đè giá trị user đã sửa).
    useEffect(() => {
        if (rowCountTouched) return
        setRowCount(computeDefaultRowCount(course, courseClass))
    }, [course, courseClass, rowCountTouched])

    // Upsert - pick-list học viên ĐÃ ghi danh trong ĐÚNG Lớp đã chọn (Lớp giờ bắt buộc, không còn
    // optional - xem CourseClassPicker). Dùng useAsyncAction (tự gọi qua useEffect bên dưới) chứ
    // KHÔNG dùng useAsync - useAsync LUÔN tự bắn request ngay lúc mount (bất kể course đã chọn hay
    // chưa, xem useAsync.js: `action && !success` luôn đúng ở lần render đầu), từng phải chèn id giả
    // 'none' để né placeholder :id chưa thay thế trong path -> BE nhận "none" làm course_id thật, ra
    // lỗi 500 (đã gặp thực tế). useAsyncAction CHỈ gọi khi handleAsyncAction() được gọi tường minh -
    // effect dưới chỉ gọi khi hasCourseAndClass && mode=upsert, không còn request rác nào cả.
    const [searchKeyword, setSearchKeyword] = useState('')
    const [selectedIds, setSelectedIds] = useState([])
    const hasCourseAndClass = !!course?.id && !!courseClass?.id
    const {response, isLoading, handleAsyncAction: fetchCourseStudents} = useAsyncAction({
        apiInfo: course_getCourseStudents_Api,
    })
    useEffect(() => {
        if (mode !== STUDENT_IMPORT_MODES.UPSERT || !hasCourseAndClass) return
        fetchCourseStudents({keyword: searchKeyword, course_class_id: courseClass.id}, {id: course.id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, hasCourseAndClass, course?.id, courseClass?.id, searchKeyword])
    const refetch = () => hasCourseAndClass && fetchCourseStudents({keyword: searchKeyword, course_class_id: courseClass.id}, {id: course.id})
    const courseStudents = (mode === STUDENT_IMPORT_MODES.UPSERT && hasCourseAndClass) ? getResponseItems(response) : []
    const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])

    // Chọn tất cả - CHỈ áp dụng cho danh sách đang hiện (vd đang lọc theo từ khoá tìm kiếm), KHÔNG
    // đụng tới lựa chọn của các học viên NGOÀI danh sách đang hiện (giữ nguyên nếu trước đó đã chọn
    // rồi mới gõ tìm kiếm khác đi) - bỏ chọn tất cả cũng chỉ bỏ đúng phần đang hiện, tương tự.
    const visibleIds = courseStudents.map((item) => item.student?.id).filter(Boolean)
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id))
    const toggleAll = () => {
        setSelectedIds((prev) => allVisibleSelected
            ? prev.filter((id) => !visibleIds.includes(id))
            : Array.from(new Set([...prev, ...visibleIds])))
    }

    const {handleAsyncAction: doExport, loading: exporting} = useAsyncAction({
        apiInfo: courseImportExport_exportTemplate_Api,
        onSuccess: (result, {response}) => {
            const url = _.get(response, 'data.url')
            const total = _.get(response, 'data.total', 0)
            if (url) window.open(url, '_blank')
            if (total === 0) message.warning(t('import export template exported empty'))
            else message.success(t('import export template exported'))
            setSelectedIds([])
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    const canExport = hasCourseAndClass && (mode === STUDENT_IMPORT_MODES.INSERT ? rowCount > 0 : selectedIds.length > 0)

    const handleExport = () => {
        const baseParams = {course_id: course.id, course_class_id: courseClass.id, mode}
        const params = mode === STUDENT_IMPORT_MODES.INSERT
            ? {...baseParams, row_count: rowCount, dedup_method: dedupMethod}
            : {...baseParams, student_ids: JSON.stringify(selectedIds)}
        doExport(params, {type: owner_type, id: owner_id, key: entity.key})
    }

    return (
        <div className="flex flex-col gap-3">
            <InfoBanner>{t('course student export hint')}</InfoBanner>

            <CourseClassPicker owner_id={owner_id} owner_type={owner_type} course={course} setCourse={setCourse} courseClass={courseClass} setCourseClass={setCourseClass} t={t}/>

            <ModeToggle value={mode} onChange={setMode} options={STUDENT_MODE_OPTIONS} t={t}/>

            {mode === STUDENT_IMPORT_MODES.INSERT ? (
                <>
                    <DedupMethodPicker value={dedupMethod} onChange={setDedupMethod} t={t}/>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('row count to create')}</span>
                        <InputNumber
                            size="large" className="w-full" min={1} max={500} value={rowCount}
                            onChange={(v) => {setRowCount(v); setRowCountTouched(true)}}
                        />
                        <span className="text-2xs text-color-400 italic leading-snug">{t('row count to create hint')}</span>
                    </div>
                </>
            ) : (
                <>
                    <div className="flex items-center gap-2">
                        <Input.Search
                            size="large" allowClear className="flex-1"
                            placeholder={t('search by name')}
                            disabled={!hasCourseAndClass}
                            onSearch={setSearchKeyword}
                        />
                        <button
                            type="button" onClick={() => refetch()} title={t('refresh')} disabled={!hasCourseAndClass}
                            className="w-10 h-10 flex-shrink-0 flex flex-center rounded-lg border border-color-200 text-color-400 hover:text-amber-700 hover:border-amber-300 cursor-pointer">
                            <IoRefreshOutline size={17}/>
                        </button>
                    </div>
                    {hasCourseAndClass && courseStudents.length > 0 && (
                        <label className="flex items-center gap-2 cursor-pointer w-fit">
                            <Checkbox checked={allVisibleSelected} onChange={toggleAll}/>
                            <span className="text-sm font-semibold text-color-000">{t('select all')} ({courseStudents.length})</span>
                        </label>
                    )}
                    <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                        {!hasCourseAndClass && <span className="text-sm text-color-300 italic text-center py-6">{t('select course and class first hint')}</span>}
                        {hasCourseAndClass && isLoading && <div className="py-6"><LoadingBlock/></div>}
                        {hasCourseAndClass && !isLoading && courseStudents.length === 0 && (
                            <span className="text-sm text-color-300 italic text-center py-6">{t('no results found')}</span>
                        )}
                        {courseStudents.map((item) => (
                            <label
                                key={item.id}
                                className="p-2.5 flex items-center gap-2.5 rounded-lg border border-color-100 hover:bg-color-50 cursor-pointer">
                                <Checkbox checked={selectedIds.includes(item.student?.id)} onChange={() => toggle(item.student?.id)}/>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-color-000 truncate">{item.student?.fullname}</div>
                                    <div className="text-xs text-color-400 truncate">
                                        {[item.student?.student_idcode, item.student?.date_of_birth ? dayjs(item.student.date_of_birth).format('DD/MM/YYYY') : null].filter(Boolean).join(' · ')}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <span className="text-sm font-semibold text-color-000">{t('selected count')}: {selectedIds.length}</span>
                </>
            )}

            <Button
                type="primary" size="large" icon={<FaFileExport size={13}/>} loading={exporting}
                disabled={!canExport}
                onClick={handleExport}>
                {t(mode === STUDENT_IMPORT_MODES.INSERT ? 'export blank template' : 'export selected template')}
            </Button>
        </div>
    )
}

// ─── Tab Nhập ───────────────────────────────────────────────────────────────────────────────────
// Dòng phụ hiện giá trị các cột tuỳ chọn (GROUP_NAME / giáo phận › giáo hạt › giáo xứ / REF_ID) nếu
// có - để admin xem trước "sẽ ghi gì" ngay ở bảng xem lại. Nhãn để nguyên tên cột (khớp file Excel
// user đang nhìn). Không có giá trị nào -> không render dòng này.
const StudentPreviewRow = ({row, t}) => {
    const geo = [row.diocese_name, row.deanery_name, row.parish_name].filter(Boolean).join(' › ')
    const extras = [
        row.group_name ? `GROUP_NAME: ${row.group_name}` : null,
        geo || null,
        row.ref_id ? `REF_ID: ${row.ref_id}` : null,
    ].filter(Boolean)

    return (
        <PreviewRowCard row={row} title={row.name} t={t}>
            <span className="text-xs text-color-400">
                {row.birth_date ? dayjs(row.birth_date).format('DD/MM/YYYY') : t('not provided yet')}
                {row.course_class_title ? ` · ${row.course_class_title}` : ''}
            </span>
            {extras.length > 0 && (
                <span className="text-2xs text-color-400 block">{extras.join('  ·  ')}</span>
            )}
            {row.match && (
                <span className={classNames('text-xs font-medium block', row.match.found ? 'text-blue-600' : 'text-emerald-600')}>
                    {row.match.found
                        ? `${t('will update existing student')}: ${row.match.student_name}`
                        : t('will create new student')}
                </span>
            )}
        </PreviewRowCard>
    )
}

export const StudentImportPanel = ({entity, owner_id, owner_type, t}) => {
    const inputRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const [preview, setPreview] = useState(null)
    const [course, setCourse] = useState(null)
    const [courseClass, setCourseClass] = useState(null)
    const [dedupMethod, setDedupMethod] = useState(DEDUP_METHOD_OPTIONS[0].value)

    const hasCourseAndClass = !!course?.id && !!courseClass?.id

    // callApi() trực tiếp (KHÔNG qua useAsyncAction) - ngoại lệ upload file đã chốt (xem
    // FamilyBookImportExportPage.js).
    const handleFile = (file) => {
        if (!file || uploading || !hasCourseAndClass) return
        setUploading(true)
        callApi({
            apiInfo: courseImportExport_previewImport_Api,
            query: bindQueryParams([{type: owner_type}, {id: owner_id}, {key: entity.key}]),
            values: {file, course_id: course.id, course_class_id: courseClass.id, dedup_method: dedupMethod},
        }).then((result) => {
            setUploading(false)
            if (inputRef.current) inputRef.current.value = ''
            if (!result) return
            if (result.error) {
                message.error(_.get(result, 'errorMessages.error') || t('error occurred'))
                return
            }
            setPreview(result.result)
        })
    }

    const {handleAsyncAction: doApply, loading: applying} = useAsyncAction({
        apiInfo: courseImportExport_applyImport_Api,
        onSuccess: (result, {response}) => {
            const data = _.get(response, 'data')
            message.success(`${t('import export applied')}: ${data?.applied ?? 0}${data?.failed ? ` (${t('error')}: ${data.failed})` : ''}`)
            setPreview(null)
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    if (!preview) {
        return (
            <div className="flex flex-col gap-3">
                <InfoBanner>{t('import export upload hint')}</InfoBanner>
                <CourseClassPicker owner_id={owner_id} owner_type={owner_type} course={course} setCourse={setCourse} courseClass={courseClass} setCourseClass={setCourseClass} t={t}/>
                <DedupMethodPicker value={dedupMethod} onChange={setDedupMethod} t={t}/>
                <InfoBanner>{t('dedup method import file hint')}</InfoBanner>
                <label className={classNames(
                    'p-8 flex flex-col items-center gap-2 rounded-xl border-2 border-dashed transition-all',
                    hasCourseAndClass ? 'border-amber-300 hover:bg-amber-50 cursor-pointer' : 'border-color-200 opacity-50 cursor-not-allowed')}>
                    <FaFileImport size={26} className="text-amber-600"/>
                    <span className="text-sm font-semibold text-color-000">
                        {!hasCourseAndClass ? t('select course and class first hint') : (uploading ? t('uploading') : t('choose file to upload'))}
                    </span>
                    <input
                        ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
                        disabled={uploading || !hasCourseAndClass}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                </label>
            </div>
        )
    }

    const validCount = preview.rows.filter((r) => r.status === 1).length

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-color-000">
                    {t('import export summary')}: {preview.valid}/{preview.total}
                </span>
                <button type="button" onClick={() => setPreview(null)} className="text-xs text-amber-700 underline cursor-pointer flex-shrink-0">
                    {t('choose another file')}
                </button>
            </div>

            {/* Cảnh báo "dán lố row" - BE gom sẵn thành 1 câu tiếng Việt (giống row.error_message,
                không cần key i18n riêng). Chỉ hiện khi có dòng nằm ngoài số dòng file mẫu. */}
            {preview.warning && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-300 text-xs text-amber-800 leading-snug">
                    ⚠ {preview.warning}
                </div>
            )}

            <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                {preview.rows.map((row) => <StudentPreviewRow key={row.id} row={row} t={t}/>)}
            </div>

            <Button
                type="primary" size="large" loading={applying} disabled={!validCount}
                onClick={() => doApply({batch_id: preview.batch_id}, {type: owner_type, id: owner_id, key: entity.key})}>
                {t('apply import')} ({validCount})
            </Button>
        </div>
    )
}
